import { SPECIALISATION_LABELS } from "@/lib/specialisation-labels";

function to12h(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  const period = (h ?? 0) >= 12 ? "PM" : "AM";
  const hour = (h ?? 0) % 12 || 12;
  return `${hour}:${String(m ?? 0).padStart(2, "0")} ${period}`;
}

// Short timezone label for a "HH:MM" local time in the given IANA zone, e.g.
// "SAST" for Africa/Johannesburg. DST-aware because Intl resolves the
// abbreviation against a concrete date. Falls back to null when no timezone is
// stored (older groups) so the caller can omit the label rather than show a
// misleading fixed offset (#383).
function timezoneAbbreviation(
  hhmm: string,
  timezone: string | null
): string | null {
  if (!timezone) {
    return null;
  }
  try {
    const [h, m] = hhmm.split(":").map(Number);
    // A fixed reference date is fine — we only need the zone abbreviation, and
    // using "today" keeps it correct across DST transitions for recurring times.
    const ref = new Date();
    ref.setHours(h ?? 0, m ?? 0, 0, 0);
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
      hour: "numeric",
    }).formatToParts(ref);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? null;
  } catch {
    return null;
  }
}

export function formatSchedule(
  day: string | null,
  startTime: string | null,
  timezone: string | null = null
): string {
  if (!(day || startTime)) {
    return "";
  }

  if (!startTime) {
    return day ?? "";
  }

  const hhmm = startTime.slice(0, 5);
  const start = to12h(hhmm);
  const label = timezoneAbbreviation(hhmm, timezone);
  const time = label ? `${start} ${label}` : start;

  return day ? `${day} at ${time}` : time;
}

export function formatFilterLabel(raw: string): string {
  return (
    SPECIALISATION_LABELS[raw] ??
    raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}
